<!--
  This is our own dropdown list to implement required field.
-->
<template>
  <el-select
    ref="dropdownElement"
    v-model="selectedValue"
    :class="{ multiple: multiple }"
    placeholder="Select one"
    :multiple="multiple"
    :clearable="clearable"
    size="large"
    @visible-change="visibleChange"
    @change="change"
  >
    <slot name="additional-options-prefix" />

    <template v-for="(item, index) in options">
      <el-option-group
        v-if="item.children"
        :key="index"
        :label="item.label"
      >
        <el-option
          v-for="(groupItem, groupIndex) in item.children"
          :key="groupIndex"
          :label="groupItem[labelKey]"
          :value="groupItem[valueKey]"
          :disabled="groupItem.disabled"
        />
      </el-option-group>
      <el-option
        v-else
        :key="`${index}-${labelKey}`"
        :label="item[labelKey]"
        :value="item[valueKey]"
        :disabled="item.disabled"
      />
    </template>

    <slot name="additional-options-suffix" />
  </el-select>
</template>

<script setup lang="ts">
import { ref, Ref, computed, WritableComputedRef, onMounted, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: [Number, String, Object, Array],
    default: null,
  },

  options: {
    type: Array<any>,
    default: () => [],
  },

  required: {
    type: Boolean,
    default: false,
  },

  valueKey: {
    type: String,
    default: 'key',
  },

  labelKey: {
    type: String,
    default: 'label',
  },

  multiple: {
    type: Boolean,
    default: false,
  },

  clearable: {
    type: Boolean,
    default: false,
  },

  type: {
    type: String,
    validator: (prop: string) => prop === 'normal' || prop === 'group',
    default: 'normal',
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const dropdownElement: Ref<any> = ref(null);

const selectedValue: WritableComputedRef<any> = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const change = (value: any) => {
  /*
    We are doing direct DOM access because it’s vue element ui select component bug.
    The problem is that that component does not set the value if we set select as multiple
    So what happened was the required validation error
  */
  if (props.multiple) {
    const inputElement = dropdownElement.value.$el.querySelector('input.el-input__inner');
    if (value && value.length > 0) {
      inputElement.value = value.join(',');
    } else {
      inputElement.value = '';
    }
  }

  emit('change', value);
};

// When dropdown disappears, readOnly attribute is added automatically by default component.
// We need to remove it b/c if input has readOnly attribute, required option doesn't work.
const visibleChange = (value: any) => {
  if (value) {
    return;
  }

  if (!props.required) {
    return;
  }

  setTimeout(() => {
    const inputElement = dropdownElement.value.$el.querySelector('input.el-input__inner');
    inputElement.readOnly = false;
    inputElement.required = true;
  }, 100);
};

const changeRequireAttribute = () => {
  if (!props.multiple) {
    return;
  }

  const inputElement = dropdownElement.value.$el.querySelector('input.el-input__inner');
  if (Array.isArray(props.modelValue) && props.modelValue.length > 0) {
    inputElement.value = props.modelValue.join(',');
  } else {
    inputElement.value = '';
  }
};

watch(() => props.modelValue, changeRequireAttribute);

onMounted(() => {
  const inputElement = dropdownElement.value.$el.querySelector('input.el-input__inner');

  if (props.required) {
    inputElement.readOnly = false;
    inputElement.required = true;
  }

  changeRequireAttribute();
});
</script>

<style lang="scss">
.select-primary.multiple {
  .el-input.el-input--suffix {
    .el-input__inner {
      color: #fff;
    }
  }
}

.el-select-dropdown {
  .el-select-group__wrap {
    margin-top: 12px;
    padding-top: 12px;
    padding-bottom: 0 !important;
    &:first-child {
      padding-top: 0;
      margin-top: 0;

      &:before {
        display: none;
      }
    }
    &:before {
      content: '';
      position: absolute;
      display: block;
      left: 20px;
      right: 20px;
      top: 0;
      height: 1px;
      background: var(--el-border-color-light);
    }
    &:after {
      display: none !important;
    }
    &:last-of-type {
      padding-bottom: 12px !important;
    }
    .el-select-group__title {
      font-size: 14px;
      font-style: italic;
      color: #c1c1c1;
    }

    .el-select-group {
      .el-select-dropdown__item {
        padding-left: 32px;
      }
    }
  }
}
</style>
